/**
 * Real OCR extraction client.
 *
 * Calls the Partner Connect backend `POST /ocr/extract` endpoint which routes the
 * uploaded file (by public URL) to Google Document AI / custom processors
 * depending on the document type, and returns a unified `fields` shape that
 * we map back onto the onboarding form sections.
 */

import { apiClient } from 'shared/lib/apiClient';
import { logger } from 'shared/lib/logger';
import type {
  BankAccountInfo,
  BusinessInfo,
  DocumentKey,
  RepresentativeInfo,
  TaxLegalInfo,
} from 'features/onboarding/types/onboarding.types';

export interface ExtractedFields {
  ownerName?: string | null;
  ownerId?: string | null;
  representativeName?: string | null;
  representativeId?: string | null;
  birthDate?: string | null;
  taxId?: string | null;
  officialBusinessName?: string | null;
  bankCode?: string | null;
  bankBranchNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountOwnerId?: string | null;
}

export interface ExtractDocumentResponse {
  docType: DocumentKey;
  fields: ExtractedFields;
  raw: Record<string, unknown>;
}

export interface ExtractionResult {
  ok: boolean;
  fields: ExtractedFields;
  business?: Partial<BusinessInfo>;
  tax?: Partial<TaxLegalInfo>;
  bank?: Partial<BankAccountInfo>;
  representative?: Partial<RepresentativeInfo>;
  raw?: Record<string, unknown>;
  /**
   * Normalized (digits-only) ת"ז / ID number extracted from this specific
   * document, used to cross-check ownership across the OCR documents.
   * Source per docType:
   *   - osekCertificate   → raw.osek.data.dealer_number
   *   - bankConfirmation  → raw.bank.data.account_holder_id
   */
  extractedIdNumber?: string;
  error?: string;
}

const isNonEmpty = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const toDigits = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return undefined;
  const digits = String(v).replace(/\D/g, '');
  return digits.length > 0 ? digits : undefined;
};

const getRawSection = (
  raw: Record<string, unknown> | undefined,
  key: string,
): Record<string, unknown> | undefined => {
  const section = raw?.[key];
  if (section && typeof section === 'object') {
    const data = (section as Record<string, unknown>).data;
    if (data && typeof data === 'object') return data as Record<string, unknown>;
  }
  return undefined;
};

const extractIdFromRaw = (
  docType: DocumentKey,
  raw: Record<string, unknown> | undefined,
): string | undefined => {
  switch (docType) {
    case 'osekCertificate': {
      const data = getRawSection(raw, 'osek');
      return toDigits(data?.dealer_number);
    }
    case 'bankConfirmation': {
      const data = getRawSection(raw, 'bank');
      return toDigits(data?.account_holder_id);
    }
    default:
      return undefined;
  }
};

/**
 * Map the backend's flat `fields` payload onto the onboarding section objects
 * that the Documents step prefills. We intentionally do NOT pick fields the
 * extractor didn't return — the step component decides whether to overwrite
 * empty form values only.
 */
const mapFieldsToSections = (
  fields: ExtractedFields,
): Pick<ExtractionResult, 'business' | 'tax' | 'bank' | 'representative'> => {
  const business: Partial<BusinessInfo> = {};
  const tax: Partial<TaxLegalInfo> = {};
  const bank: Partial<BankAccountInfo> = {};
  const representative: Partial<RepresentativeInfo> = {};

  if (isNonEmpty(fields.ownerName)) business.ownerName = fields.ownerName;
  if (isNonEmpty(fields.taxId)) tax.taxId = fields.taxId;
  if (isNonEmpty(fields.officialBusinessName)) {
    tax.officialBusinessName = fields.officialBusinessName;
    // Also seed the commercial / restaurant name shown in Step 2 — the
    // "כינוי העסק" line on the Osek certificate is the business's trading name.
    business.commercialName = fields.officialBusinessName;
  }
  if (isNonEmpty(fields.ownerId)) bank.bankAccountOwnerId = fields.ownerId;
  if (isNonEmpty(fields.bankBranchNumber))
    bank.bankBranchNumber = fields.bankBranchNumber;
  if (isNonEmpty(fields.bankAccountNumber))
    bank.bankAccountNumber = fields.bankAccountNumber;
  if (isNonEmpty(fields.bankAccountOwnerId))
    bank.bankAccountOwnerId = fields.bankAccountOwnerId;
  if (isNonEmpty(fields.representativeName))
    representative.representativeName = fields.representativeName;
  if (isNonEmpty(fields.representativeId))
    representative.representativeId = fields.representativeId;

  return {
    business: Object.keys(business).length ? business : undefined,
    tax: Object.keys(tax).length ? tax : undefined,
    bank: Object.keys(bank).length ? bank : undefined,
    representative: Object.keys(representative).length ? representative : undefined,
  };
};

export const extractDocument = async (params: {
  docType: DocumentKey;
  url: string;
  useDocumentAi?: boolean;
}): Promise<ExtractionResult> => {
  try {
    const res = await apiClient.post<ExtractDocumentResponse>('/ocr/extract', {
      docType: params.docType,
      url: params.url,
      useDocumentAi: params.useDocumentAi,
    });

    const fields = res.data.fields ?? {};
    const extractedIdNumber = extractIdFromRaw(params.docType, res.data.raw);
    return {
      ok: true,
      fields,
      raw: res.data.raw,
      extractedIdNumber,
      ...mapFieldsToSections(fields),
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Unknown OCR extraction error';
    logger.warn('[HAAT Partner Connect] OCR extraction failed', err);
    return {
      ok: false,
      fields: {},
      error: message,
    };
  }
};
